import { createAction } from '@reduxjs/toolkit';

import type { AppDispatch } from 'Core/store';
import { SearchItemData, SearchItemDataCollection } from 'Models/index';
import { keys, loadCompared, loadQueriesHistory, removeItem, setItem } from 'Utils/userStorage';

// ---- action creators (exported for reducers to use in addCase) ----

export const setCompared = createAction('SET_COMPARED', (compared: SearchItemDataCollection) => ({
  payload: compared,
}));

const setQueriesHistoryAction = createAction('SET_QUERIES_HISTORY', (queriesHistory: string[]) => ({
  payload: queriesHistory,
}));

const clearQueriesHistoryAction = createAction('CLEAR_QUERIES_HISTORY');

export const clearHistoryAction = createAction('CLEAR_HISTORY');

// ---- search history thunks ----

const historySize = 5;

export function setQueriesHistory(query: string): (dispatch: AppDispatch) => void {
  return (dispatch: AppDispatch) => {
    const queriesHistory = loadQueriesHistory().filter((v) => v !== query);

    queriesHistory.unshift(query);
    if (queriesHistory.length > historySize) {
      queriesHistory.pop();
    }

    setItem(keys.queriesHistory, queriesHistory);
    dispatch(setQueriesHistoryAction(queriesHistory));
  };
}

export const clearQueriesHistory = (): ReturnType<typeof clearQueriesHistoryAction> => {
  removeItem(keys.queriesHistory);
  return clearQueriesHistoryAction();
};

export const clearHistory = (): ReturnType<typeof clearHistoryAction> => {
  removeItem(keys.queriesHistory);
  removeItem(keys.productsHistory);
  return clearHistoryAction();
};

// ---- compared items thunks ----

export const toggleCompared =
  (item: SearchItemData): ((dispatch: AppDispatch) => void) =>
  (dispatch: AppDispatch) => {
    const compared = loadCompared();

    const isItemsUnequal = (i: typeof item.plainObject) => SearchItemData.getFromRaw(i, 'key') !== item.key;
    const newCompared = compared.every(isItemsUnequal)
      ? [...compared, item.plainObject]
      : compared.filter(isItemsUnequal);

    setItem(keys.compared, newCompared);
    dispatch(setCompared(new SearchItemDataCollection(newCompared, 'compared')));
  };

export const clearCompared = (): ((dispatch: AppDispatch) => void) => (dispatch: AppDispatch) => {
  setItem(keys.compared, null);
  dispatch(setCompared(new SearchItemDataCollection([], 'compared')));
};

// Re-export internal creators so reducers can use them in addCase without making them public API.
export { clearQueriesHistoryAction, setQueriesHistoryAction };
